import React, { useEffect, useRef, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    Typography,
} from '@mui/material';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import { Info } from 'lucide-react';
import {
    SecurityIdentityFieldMenu,
    securityIdentityFieldLabel,
} from '../commonComponents/SecurityIdentityFieldSelector';
import DataTable from '../data_table/DataTable';
import IconButton from '../ui/IconButton';
import Tooltip from '../ui/Tooltip';
import { MSI_V2_STAGED_IDENTIFIER_COLUMNS } from '../../config/UploadConstant';
import {
    deleteOptimizerPortfolio,
    previewOptimizerWatchlistRestoration,
    stageOptimizerWatchlistRestoration,
} from '../../utils/ApiCall';
import {
    LARGEST_TRADE_FLOOR_PCT_AUM,
    createOptimizerOperationId,
} from '../../utils/optimizerConstraintEditor';
import { securityIdentityPresentation } from '../../utils/securityIdentityPresentation';
import { watchlistVersionLabel } from '../../utils/watchlistVersionLabels';
import {
    deleteOptimizerPortfolioAndLoadList,
    isDefiniteOptimizerMutationRejection,
} from './optimizerPortfolioMutationContract';

const RESTORATION_IDENTIFIER_TYPES = new Set(MSI_V2_STAGED_IDENTIFIER_COLUMNS);

function validRestorationRevision(revision) {
    return Boolean(
        revision
        && typeof revision === 'object'
        && typeof revision.revision_id === 'string'
        && revision.revision_id.trim().length > 0
        && Number.isInteger(revision.revision_seq)
        && revision.revision_seq > 0
        && typeof revision.published_at === 'string'
        && !Number.isNaN(Date.parse(revision.published_at))
    );
}

function restorationPresentationRow(security) {
    return {
        ...security,
        inbound_id_type: security?.submitted_identifier?.type,
        inbound_id_value: security?.submitted_identifier?.raw_value,
        inbound_id_value_raw: security?.submitted_identifier?.raw_value,
    };
}

function restorationIdentityPresentation(security, field) {
    const row = restorationPresentationRow(security);
    const primary = securityIdentityPresentation(row, field);
    if (primary.value !== '—') return primary;
    if (field === 'company_name') {
        const ticker = securityIdentityPresentation(row, 'ticker');
        if (ticker.value !== '—') return ticker;
    }
    return securityIdentityPresentation(row, 'submitted_identifier');
}

function revisionTimestamp(revision) {
    const raw = revision?.published_at;
    const parsed = raw ? new Date(raw) : null;
    return parsed && !Number.isNaN(parsed.getTime()) ? parsed.toLocaleString() : '—';
}

function SecurityDetails({ security, identityLabel }) {
    const attributes = Object.entries(security?.attributes || {});
    const attributeNames = new Set(
        attributes.map(([name]) => String(name).trim().toLowerCase()),
    );
    const label = `Show Watchlist details for ${identityLabel}`;
    return (
        <Tooltip
            placement="left"
            content={(
                <span className="block py-0.5 text-[0.75rem] leading-[1.66]">
                    {!attributeNames.has('country') ? (
                        <span className="block">Country: {security.country_hint || '—'}</span>
                    ) : null}
                    {!attributeNames.has('mic') ? (
                        <span className="block">MIC: {security.mic_hint || '—'}</span>
                    ) : null}
                    {security.saved_optimized_quantity != null && (
                        <span className="block">
                            Saved optimized shares: {Number(security.saved_optimized_quantity).toLocaleString(undefined, { maximumFractionDigits: 6 })}
                        </span>
                    )}
                    {attributes.map(([name, value]) => (
                        <span key={name} className="block">{name}: {value}</span>
                    ))}
                </span>
            )}
        >
            <IconButton size="xs" label={label}>
                <Info aria-hidden="true" size={16} strokeWidth={2} />
            </IconButton>
        </Tooltip>
    );
}

export function optimizerWatchlistSelectionNeedsResolution(portfolio) {
    const compatibility = portfolio?.compatibility;
    return Boolean(
        portfolio?.id != null
        && compatibility?.status === 'partially_available'
        && compatibility?.reason === 'watchlist_membership_changed'
        && Number(compatibility?.missing_security_count) > 0
    );
}

export function optimizerWatchlistRestorationRequired(portfolio) {
    const compatibility = portfolio?.compatibility;
    return Boolean(
        optimizerWatchlistSelectionNeedsResolution(portfolio)
        && portfolio?.portfolioVersion != null
        && compatibility?.restoration_status === 'preview_required'
        && compatibility?.current_watchlist_revision_id
        && compatibility?.source_watchlist_revision_id
    );
}

export function optimizerPortfolioRecoveryAvailable(portfolio) {
    return Boolean(portfolio?.type === 'opt' && portfolio?.portfolioVersion
        && portfolio?.receiptId && portfolio?.compatibility?.current_watchlist_revision_id
        && portfolio?.compatibility?.source_watchlist_revision_id);
}

function optimizerWatchlistRestorationKey(portfolio) {
    if (!optimizerPortfolioRecoveryAvailable(portfolio)) return '';
    return [
        portfolio.id,
        portfolio.portfolioVersion,
        portfolio.compatibility.current_watchlist_revision_id,
        portfolio.compatibility.source_watchlist_revision_id,
    ].join(':');
}

function requireOptimizerWatchlistRestorationPreview(response, expected) {
    if (response?.success === false) {
        const backendMessage = typeof response.message === 'string'
            ? response.message.trim()
            : '';
        throw new Error(
            backendMessage && !backendMessage.startsWith('Cloud Run call failed:')
                ? backendMessage
                : 'The original Watchlist inputs could not be loaded.',
        );
    }
    const securities = response?.securities;
    const validSecurity = (security) => {
        const identifier = security?.submitted_identifier;
        return Boolean(
            security
            && typeof security === 'object'
            && typeof security.cognive_id === 'string'
            && security.cognive_id.length > 0
            && security.cognive_id.trim() === security.cognive_id
            && RESTORATION_IDENTIFIER_TYPES.has(identifier?.type)
            && typeof identifier?.raw_value === 'string'
            && identifier.raw_value.trim().length > 0
            && security.attributes
            && typeof security.attributes === 'object'
            && !Array.isArray(security.attributes)
            && (
                security.msi_listing_label == null
                || (
                    typeof security.msi_listing_label === 'object'
                    && !Array.isArray(security.msi_listing_label)
                )
            )
            && validRestorationRevision(security.last_watchlist_revision)
        );
    };
    if (
        response?.contract !== 'optimizer-watchlist-restoration.v1'
        || !['available', 'not_needed'].includes(response?.status)
        || response?.portfolio_id !== expected.portfolioId
        || response?.portfolio_version !== expected.portfolioVersion
        || typeof response?.current_watchlist_revision_id !== 'string'
        || !response.current_watchlist_revision_id.trim()
        || response.current_watchlist_revision_id.trim() !== response.current_watchlist_revision_id
        || response?.source_watchlist_revision_id !== expected.sourceWatchlistRevisionId
        || !Number.isInteger(response?.missing_security_count)
        || response.missing_security_count < 0
        || (response.status === 'available') !== (response.missing_security_count > 0)
        || !Array.isArray(securities)
        || securities.length !== response.missing_security_count
        || securities.some((security) => !validSecurity(security))
    ) {
        throw new Error('The exact original Watchlist inputs are unavailable.');
    }
    return response;
}

// Watchlist recovery does not repair or recompute saved optimized holdings.
function optimizerRecoveryRequired(preview) {
    return preview?.missing_security_count > 0;
}

export function useOptimizerWatchlistRestoration({
    open,
    portfolio,
    onClose,
    onError,
    onDeleted,
    onStaged,
    fetchOptimizerPortfolios,
}) {
    const [preview, setPreview] = useState(null);
    const [loading, setLoading] = useState(false);
    const [staging, setStaging] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [dialogError, setDialogError] = useState('');
    const recoveryRequired = optimizerRecoveryRequired(preview);
    const attemptIdRef = useRef(null);
    const generationRef = useRef(0);
    const previewKeyRef = useRef('');
    const restorationKey = optimizerWatchlistRestorationKey(portfolio);
    const restorationIdentityRef = useRef(null);
    if (restorationIdentityRef.current?.key !== restorationKey) {
        restorationIdentityRef.current = restorationKey
            ? {
                key: restorationKey,
                portfolioId: portfolio.id,
                portfolioVersion: portfolio.portfolioVersion,
                sourceWatchlistRevisionId: (
                    portfolio.compatibility.source_watchlist_revision_id
                ),
            }
            : null;
    }
    const onErrorRef = useRef(onError);
    onErrorRef.current = onError;

    useEffect(() => {
        generationRef.current += 1;
        const generation = generationRef.current;
        setPreview(null);
        setDialogError('');
        attemptIdRef.current = null;
        previewKeyRef.current = '';
        const identity = restorationIdentityRef.current;
        if (!open || !identity) {
            setLoading(false);
            setStaging(false);
            setDeleting(false);
            return undefined;
        }

        setLoading(true);
        setStaging(false);
        setDeleting(false);
        (async () => {
            try {
                const response = requireOptimizerWatchlistRestorationPreview(
                    await previewOptimizerWatchlistRestoration({
                        portfolioId: identity.portfolioId,
                        expectedPortfolioVersion: identity.portfolioVersion,
                    }),
                    {
                        portfolioId: identity.portfolioId,
                        portfolioVersion: identity.portfolioVersion,
                        sourceWatchlistRevisionId: identity.sourceWatchlistRevisionId,
                    },
                );
                if (generation !== generationRef.current) return;
                attemptIdRef.current = createOptimizerOperationId();
                previewKeyRef.current = identity.key;
                setPreview(response);
            } catch (error) {
                if (generation !== generationRef.current) return;
                const message = error?.message || String(error);
                setDialogError(message);
                onErrorRef.current?.(message);
            } finally {
                if (generation === generationRef.current) {
                    setLoading(false);
                }
            }
        })();

        return () => {
            generationRef.current += 1;
        };
    }, [open, restorationKey]);

    const handleStage = async () => {
        const identity = restorationIdentityRef.current;
        if (
            loading || staging || deleting || !preview || !identity
            || previewKeyRef.current !== identity.key
            || !recoveryRequired
        ) return;
        const generation = generationRef.current;
        // The read-only preview discovers the current Watchlist, which may have
        // advanced since the dropdown loaded. Stage only against that preview.
        const currentWatchlistRevisionId = preview.current_watchlist_revision_id;
        const recoveryAttemptId = attemptIdRef.current || createOptimizerOperationId();
        setLoading(true);
        setStaging(true);
        setDialogError('');
        try {
            const response = await stageOptimizerWatchlistRestoration({
                portfolioId: identity.portfolioId,
                expectedPortfolioVersion: identity.portfolioVersion,
                expectedWatchlistRevisionId: currentWatchlistRevisionId,
                attemptId: recoveryAttemptId,
            });
            if (
                response?.contract !== 'optimizer-watchlist-restoration.v1'
                || !['pending_review', 'not_needed'].includes(response?.status)
                || typeof response?.watchlist_revision_id !== 'string'
                || !response.watchlist_revision_id
                || response?.current_watchlist_revision_id
                    !== currentWatchlistRevisionId
                || response?.source_watchlist_revision_id
                    !== identity.sourceWatchlistRevisionId
            ) {
                throw new Error(
                    response?.message || 'The Watchlist review could not be staged.',
                );
            }
            if (generation === generationRef.current) {
                attemptIdRef.current = null;
                previewKeyRef.current = '';
                setPreview(null);
            }
            // A committed pending review must still be surfaced if selection
            // changes while staging. The dashboard fences the session owner.
            onStaged?.(response, {
                portfolio,
                attemptId: recoveryAttemptId,
            });
        } catch (error) {
            if (generation === generationRef.current) {
                const message = error?.message || String(error);
                setDialogError(message);
                onErrorRef.current?.(message);
            }
        } finally {
            if (generation === generationRef.current) {
                setLoading(false);
                setStaging(false);
            }
        }
    };

    const handleDelete = async () => {
        const portfolioId = portfolio?.id;
        if (
            staging
            || deleting
            || portfolioId == null
            || typeof fetchOptimizerPortfolios !== 'function'
        ) return;
        const generation = generationRef.current;
        const portfolioName = String(portfolio?.value || portfolio?.name || 'Portfolio');
        let mutationAttempted = false;
        let barrierHandle = null;
        setDeleting(true);
        setDialogError('');
        try {
            const blocked = await fetchOptimizerPortfolios({
                mutation: {
                    mode: 'block',
                    barrier: {
                        portfolioId,
                        portfolioName,
                        expected: 'absent',
                    },
                },
            });
            if (blocked?.status !== 'blocked' || !blocked?.barrierHandle) {
                throw new Error('Portfolio deletion could not establish a mutation barrier.');
            }
            barrierHandle = blocked.barrierHandle;
            mutationAttempted = true;
            await deleteOptimizerPortfolioAndLoadList({
                portfolioId,
                deleteRequest: deleteOptimizerPortfolio,
                listRequest: () => fetchOptimizerPortfolios({
                    force: true,
                    mutation: {
                        mode: 'commit-and-reconcile',
                        barrierHandle,
                    },
                }),
            });
            if (generation !== generationRef.current) return;
            previewKeyRef.current = '';
            setPreview(null);
            onDeleted?.({
                portfolioId,
                portfolioName,
                refreshRequired: false,
            });
        } catch (error) {
            const mutationRejected = Boolean(
                mutationAttempted
                && !error?.optimizerMutationCommitted
                && isDefiniteOptimizerMutationRejection(error),
            );
            if (mutationRejected) {
                await fetchOptimizerPortfolios({
                    mutation: { mode: 'release', barrierHandle },
                });
            } else if (mutationAttempted && !error?.optimizerMutationCommitted) {
                await fetchOptimizerPortfolios({
                    mutation: { mode: 'mark-unknown', barrierHandle },
                });
            }
            if (generation !== generationRef.current) return;
            const message = error?.optimizerMutationCommitted
                ? 'Portfolio was deleted, but the saved portfolio list could not be refreshed. Reload the application before continuing.'
                : mutationAttempted && !mutationRejected
                    ? 'The delete outcome could not be confirmed. Do not retry. Reload the application and inspect the saved portfolio.'
                    : `The portfolio could not be deleted. ${error?.message || error}`;
            setDialogError(message);
            onErrorRef.current?.(message);
            if (error?.optimizerMutationCommitted) {
                onDeleted?.({
                    portfolioId,
                    portfolioName,
                    refreshRequired: true,
                });
            }
        } finally {
            if (generation === generationRef.current) {
                setDeleting(false);
            }
        }
    };

    const handleClose = () => {
        if (!staging && !deleting) onClose?.();
    };

    return {
        preview, loading, staging, deleting, dialogError, recoveryRequired,
        handleStage, handleDelete, handleClose,
    };
}

// The missing-securities table: fixed 36/24/28% columns and an 80px Details
// column (room for its header), scrolling sideways below 660px; 32px rows.
const PREVIEW_COLUMN_MIN_WIDTH = 660;
const previewColumnSize = (share) => Math.round((PREVIEW_COLUMN_MIN_WIDTH * share) / 100);
const getSecurityRowId = (row) => row.cognive_id;

/**
 * The securities a saved portfolio holds that are no longer in the
 * Watchlist, with their last Watchlist version. The table grows with its rows
 * up to `maxHeight`; `className` styles its frame instead (e.g. a responsive
 * max-height), replacing `maxHeight`.
 */
export function OptimizerWatchlistRestorationPreview({
    preview,
    loading,
    dialogError,
    maxHeight = 360,
    className,
}) {
    const [identityField, setIdentityField] = useState('company_name');

    useEffect(() => {
        setIdentityField('company_name');
    }, [preview]);

    const savedQuantities = preview?.saved_optimized_quantities;
    const columns = [
        {
            id: 'identity',
            header: securityIdentityFieldLabel(identityField),
            enableSorting: false,
            minSize: previewColumnSize(36),
            cell: ({ row: { original } }) => {
                const identity = restorationIdentityPresentation(original, identityField);
                return (
                    <Tooltip content={identity.tooltip || identity.value}>
                        <span className="block truncate">{identity.value}</span>
                    </Tooltip>
                );
            },
            meta: {
                width: '36%',
                headerControls: () => (
                    <SecurityIdentityFieldMenu value={identityField} onChange={setIdentityField} />
                ),
            },
        },
        {
            id: 'version',
            header: 'Last Watchlist version',
            enableSorting: false,
            minSize: previewColumnSize(24),
            accessorFn: (row) => watchlistVersionLabel(row.last_watchlist_revision) || '—',
            meta: { width: '24%' },
        },
        {
            id: 'published',
            header: 'Published',
            enableSorting: false,
            minSize: previewColumnSize(28),
            accessorFn: (row) => revisionTimestamp(row.last_watchlist_revision),
            meta: { width: '28%' },
        },
        {
            id: 'details',
            header: 'Details',
            enableSorting: false,
            minSize: 80,
            cell: ({ row: { original } }) => (
                <SecurityDetails
                    security={{ ...original, saved_optimized_quantity: savedQuantities?.[original.cognive_id] }}
                    identityLabel={restorationIdentityPresentation(original, identityField).value}
                />
            ),
            meta: { width: 80, align: 'center', wrap: true },
        },
    ];

    return (
        <>
            {loading && !preview ? (
                <Box
                    sx={{ minHeight: 120, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}
                >
                    <CircularProgress size={20} />
                    <Typography variant="body2">Loading original Watchlist inputs…</Typography>
                </Box>
            ) : null}
            {dialogError ? (
                <Alert aria-label="Watchlist restoration error" severity="error" sx={{ mb: 2 }}>
                    {dialogError}
                </Alert>
            ) : null}
            {preview?.securities?.length ? (
                <DataTable
                    aria-label="Missing Watchlist securities"
                    data={preview.securities}
                    columns={columns}
                    getRowId={getSecurityRowId}
                    maxHeight={className ? undefined : maxHeight}
                    className={className}
                    rowHeight={32}
                    headerHeight={34}
                />
            ) : null}
        </>
    );
}

// Re-optimizing starts from the saved portfolio's own baseline as it is now.
export function OptimizerZeroOverlapWarning({ baselineSourceMode = 'actual' }) {
    return <Alert severity="warning">
        None of this portfolio’s securities is in your current
        {baselineSourceMode === 'pos_sizing' ? ' Position Sizing draft' : ' Actual portfolio'},
        so re-optimizing starts each of them at zero shares. With Largest trade on, each trade
        is then measured against {LARGEST_TRADE_FLOOR_PCT_AUM}% of AUM, so at 50% each position
        can reach only {LARGEST_TRADE_FLOOR_PCT_AUM / 2}% of AUM. Review the saved selection and
        limits before continuing.
    </Alert>;
}

export function OptimizerRecoveryAction({ restoration, disabled = false, disabledReason = null }) {
    return <Stack role="group" aria-label="Portfolio recovery" spacing={0.5}
        sx={{ maxWidth: 620, mx: 'auto', '& .MuiButton-root': { minHeight: 32, py: 0.5 } }}>
        <Button fullWidth variant="contained" disabled={disabled}
            onClick={() => restoration.handleStage()}>Recover Watchlist</Button>
        {disabled && disabledReason && <Typography variant="caption" color="warning.main" display="block" sx={{ lineHeight: 1.4 }}>
            {disabledReason}
        </Typography>}
        <Typography variant="caption" color="text.secondary" display="block" sx={{ lineHeight: 1.4 }}>
            Adds the missing securities back to your Watchlist after you review the change.
            Then use Re-optimize separately in the saved portfolio’s details. Saved holdings,
            Actual and Position Sizing stay unchanged.
        </Typography>
    </Stack>;
}

export default function OptimizerWatchlistRestorationDialog(props) {
    const restoration = useOptimizerWatchlistRestoration(props);
    const {
        preview, loading, staging, deleting, dialogError, recoveryRequired, handleDelete, handleClose,
    } = restoration;
    return <Dialog open={Boolean(props.open)} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>{props.portfolio?.value || 'Recover Watchlist'}</DialogTitle>
        <DialogContent>
            {(!preview || recoveryRequired) && <Alert severity="info" sx={{ mb: 2 }}>
                Recover the missing Watchlist securities before re-optimizing. Changes are
                reviewed before they apply.
            </Alert>}
            <OptimizerWatchlistRestorationPreview preview={preview} loading={loading}
                dialogError={dialogError} maxHeight={240} />
            {(!preview || recoveryRequired) ? (
                <Box sx={{ mt: 2 }}><OptimizerRecoveryAction restoration={restoration}
                    disabled={loading || staging || deleting || !preview} /></Box>
            ) : <Typography color="text.secondary">No Watchlist recovery is needed.</Typography>}
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between' }}>
            <Button color="error" startIcon={<DeleteOutlineRoundedIcon />} onClick={handleDelete}
                disabled={loading || staging || deleting || props.portfolio?.id == null
                    || typeof props.fetchOptimizerPortfolios !== 'function'}>Delete optimized portfolio</Button>
            <Button onClick={handleClose} disabled={staging || deleting}>Close</Button>
        </DialogActions>
    </Dialog>;
}
